import React from "react";

import { AngelouTermSheet } from "@newsela/angelou";
import PropTypes from "prop-types";

import TanStackQueryProvider from "@/base/TanStackQueryProvider/TanStackQueryProvider";
import ApplicationErrorBoundary from "@/errors/ApplicationErrorBoundary";
import EventTrackingProviderSnitch from "@/eventStream/snitch/EventTrackingProviderSnitch";
import { GetPreviousUrlProvider } from "@/eventStream/useGetPreviousUrl/useGetPreviousUrl";
import { EventTrackingProvider } from "@/eventStream/useTracker/useTracker";
import { SupportChatProvider } from "@/login/SupportChatProvider";
import { UserProvider } from "@/users/useUser";
import { UserAttributesProvider } from "@/users/useUserAttributes";
import { UserExperimentSegmentsProvider } from "@/users/useUserExperimentSegments";
import shapeServerResponse from "@/utils/shapeServerResponse";
import { StableUniqueIdProvider } from "@/utils/useStableUniqueId";

const propTypes = {
  userProviderProps: PropTypes.shape(UserProvider.propTypes),
  userExperimentSegmentsProviderProps: PropTypes.object,
  children: PropTypes.node,
  dehydratedState: PropTypes.object,
};

const angelouTerms = {
  BradburyBadge: {
    text: "Advisory",
  },
  BookmarkButton: {
    inactiveScreenReaderText: "Save content",
    activeScreenReaderText: "Remove from your content",
  },
};

const RootProviders = ({
  userProviderProps,
  userExperimentSegmentsProviderProps,
  children,
  dehydratedState,
}) => {
  return (
    <ApplicationErrorBoundary>
      <TanStackQueryProvider dehydratedState={dehydratedState}>
        <GetPreviousUrlProvider>
          <StableUniqueIdProvider>
            <UserProvider {...userProviderProps}>
              <UserExperimentSegmentsProvider
                {...userExperimentSegmentsProviderProps}
              >
                <UserAttributesProvider>
                  <EventTrackingProvider>
                    <EventTrackingProviderSnitch>
                      <AngelouTermSheet terms={angelouTerms}>
                        <SupportChatProvider>{children}</SupportChatProvider>
                      </AngelouTermSheet>
                    </EventTrackingProviderSnitch>
                  </EventTrackingProvider>
                </UserAttributesProvider>
              </UserExperimentSegmentsProvider>
            </UserProvider>
          </StableUniqueIdProvider>
        </GetPreviousUrlProvider>
      </TanStackQueryProvider>
    </ApplicationErrorBoundary>
  );
};

RootProviders.__getInitialProps = async ({
  ctx,
  getReducedUserShape = false,
}) => {
  const [userProviderProps, userExperimentSegmentsProviderProps] =
    await Promise.all([
      UserProvider.__getInitialProps({ ctx, getReducedUserShape }),
      UserExperimentSegmentsProvider.__getInitialProps({ ctx }),
    ]);

  return shapeServerResponse({
    userProviderProps,
    userExperimentSegmentsProviderProps,
  });
};

RootProviders.propTypes = propTypes;

// Pages that are being SSR may find useful to use a common util method called __getInitialProps to further populate our Providers with data from the server before hydration.
// By calling this method within, e.g., getServerSideProps you may pass down via pageProps the information we want to serve to the to be hydrated RootProviders.
// Within pageProps we expect a property whose key-value pair are of the form [RootProviders.InitialPropsName]: RootProvidersInitialProps

RootProviders.InitialPropsName = "RootProvidersInitialProps";

export default RootProviders;
